/**
 * 数据源与数据集探查。
 * 两套端点：
 *  - datasourceAction (/baskserver/api/datasource/...)：列数据源、列已登记数据集、取字段、取样例。
 *  - databaseAction  (/baskserver/baskreport/api/datasource-database/...)：贴近设计器，
 *    用于列举表/视图（buildDatabaseTables）、按 SQL 构建字段（buildFields）、列内建数据源。
 * 鉴权：@BaskAuthorization(model="datasources", code="manager")
 *
 * buildDatabaseTables / buildFields 为表单参数（application/x-www-form-urlencoded），
 * 经实测契约如下：
 *   buildDatabaseTables?online=false&tenantId=<tenant>
 *     body: id=<datasourceId> sql= type=server datasourceType=jdbc
 *           parameters={} parameterTypes={} tableType=TABLE
 *   buildFields?limit=30&online=false&tenantId=<tenant>
 *     body: id=<datasourceId> sql=<select ...> type=server datasourceType=jdbc
 *           datasetId={} parameters={} parameterTypes={}
 */

import { Session, authedRequest, parseJson, ApiError } from './session.js';
import {
  datasourceAction,
  databaseAction,
  serverDatasourceAction,
  beanDatasourceAction,
  designerAction,
} from './urls.js';

export interface DatasourceInfo {
  name: string;
  type?: string;
  [key: string]: any;
}

export interface DatasetInfo {
  id?: number;
  name: string;
  type?: string;
  [key: string]: any;
}

export interface FieldInfo {
  name: string;
  label?: string;
  type?: string;
  [key: string]: any;
}

export interface DatasourceDef {
  id?: string | number;
  name?: string;
  type?: string;
  platform?: string;
  driver?: string;
  url?: string;
  username?: string;
  [key: string]: any;
}

/** 列出某数据源下的数据集（表/视图由 datasourceId 即时映射，无整型 id）。 */
export async function listDatasets(
  session: Session,
  datasourceId: string | number,
): Promise<DatasetInfo[]> {
  const url = `${datasourceAction(session.baseUrl, 'datasets')}?datasourceId=${datasourceId}`;
  const resp = await authedRequest(session, url, { method: 'GET' });
  if (resp.statusCode !== 200) throw new ApiError(`列数据集失败 (${resp.statusCode})`, resp.statusCode);
  const json = await parseJson(resp);
  return json.data ?? json;
}

/**
 * 远程数据源库：列举某数据源下的表/视图（或 API 数据源下的数据集）。
 * 表单参数（经实测契约）：id / sql / type=server / datasourceType=jdbc|api /
 *   parameters={} / parameterTypes={} / tableType=TABLE
 *   - JDBC：datasourceType=jdbc，返回表/视图名（字符串或 {name,type}）。
 *   - API ：datasourceType=api，返回 { id: <datasetId>, name, type:"API" } 数组。
 */
export async function buildDatabaseTables(
  session: Session,
  datasourceId: string,
  opts: { online?: boolean; tenantId: string; datasourceType?: string },
): Promise<{ name: string; type?: string; id?: string }[]> {
  const online = opts.online ?? false;
  const tenantId = opts.tenantId;
  const datasourceType = opts.datasourceType ?? 'jdbc';
  const url = `${databaseAction(session.baseUrl, 'buildDatabaseTables')}?online=${online}&tenantId=${tenantId}`;
  const body = new URLSearchParams({
    id: datasourceId,
    sql: '',
    type: 'server',
    datasourceType,
    parameters: '{}',
    parameterTypes: '{}',
    tableType: 'TABLE',
  }).toString();
  const resp = await authedRequest(session, url, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded; charset=UTF-8' },
    body,
  });
  if (resp.statusCode !== 200) throw new ApiError(`列举表失败 (${resp.statusCode})`, resp.statusCode);
  const json = await parseJson(resp);
  const data = json.data ?? json;
  const tables = Array.isArray(data) ? data : (data.tables ?? []);
  return tables;
}

/**
 * 构造表单参数（buildFields 用）。
 * datasourceType：jdbc | api，默认 jdbc；API 数据源不发 sql（无 select 语义）。
 * datasetId：JDBC 时为 '{}'，API 时为真实的 datasetId（字符串/整型）。
 */
function databaseForm(
  datasourceId: string,
  opts: {
    sql?: string;
    datasourceType?: string;
    datasetId?: string | number;
  },
): string {
  const datasourceType = opts.datasourceType ?? 'jdbc';
  const isApi = datasourceType === 'api';
  return new URLSearchParams({
    id: datasourceId,
    sql: isApi ? '' : (opts.sql ?? ''),
    type: 'server',
    datasourceType,
    datasetId: opts.datasetId != null ? String(opts.datasetId) : '{}',
    parameters: '{}',
    parameterTypes: '{}',
  }).toString();
}

/**
 * 远程数据源库：构建字段（含字段数组 fields[] 与样例数据 data[]）。
 *  - JDBC：传 sql=select ...，datasetId={}。
 *  - API ：传 datasourceType=api 与 datasetId=<真实 id>，sql 留空（按数据集探列）。
 * 返回 { fields: FieldInfo[], rows: any[] }。
 */
export async function buildFields(
  session: Session,
  datasourceId: string,
  sql: string,
  opts: {
    online?: boolean;
    /** 租户 ID；缺省时回退到 session.tenantId。 */
    tenantId?: string;
    limit?: number;
    datasourceType?: string;
    datasetId?: string | number;
  },
): Promise<{ fields: FieldInfo[]; rows: any[] }> {
  const online = opts.online ?? false;
  const tenantId = opts.tenantId ?? session.tenantId;
  if (!tenantId) throw new ApiError('buildFields 需要 tenantId（可通过 opts.tenantId 或 session.tenantId 提供）', 400);
  const limit = opts.limit ?? 30;
  const url = `${databaseAction(session.baseUrl, 'buildFields')}?limit=${limit}&online=${online}&tenantId=${tenantId}`;
  const body = databaseForm(datasourceId, {
    sql,
    datasourceType: opts.datasourceType,
    datasetId: opts.datasetId,
  });
  const resp = await authedRequest(session, url, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded; charset=UTF-8' },
    body,
  });
  if (resp.statusCode !== 200) throw new ApiError(`构建字段失败 (${resp.statusCode})`, resp.statusCode);
  const json = await parseJson(resp);
  // 实测响应为 { fields: [...], data: [...] }，fields 在顶层、data 就是数据行。
  // 不能先做 `json.data ?? json` 再取 .fields —— 那样会下钻进数据行数组导致 fields 恒为空。
  // 同时兼容外层包一层 data 的形态：{ data: { fields: [...], data: [...] } }。
  const envelope = Array.isArray(json?.fields) ? json : (json?.data ?? json);
  const fields = envelope?.fields ?? [];
  const rows = Array.isArray(envelope?.data)
    ? envelope.data
    : (envelope?.rows ?? (Array.isArray(json?.data) ? json.data : []));
  return { fields, rows };
}

export interface TableSchema {
  name: string;
  type?: string;
  columns: { name: string; type?: string }[];
  /** 探查该表失败时的原因（此时 columns 为空）。 */
  error?: string;
}

/**
 * 一次性探查数据源下所有表的列结构，供「写 SQL 之前」拿到真实 schema。
 *
 * 实现：先 buildDatabaseTables 列出表/数据集，再逐张探查列结构。
 *  - JDBC：对每张表 buildFields(`select * from t`, limit=1) 取回列名与类型。
 *  - API ：用 buildDatabaseTables 返回的 datasetId，buildFields(datasourceType=api,
 *          datasetId=<id>) 取回列名与类型（无 select 语义，sql 留空）。
 * 服务端没有批量 schema 端点，只能逐表探查，故用 concurrency 控制并发，
 * 单表失败不中断整体（记入 error 字段）。
 */
export async function buildSchema(
  session: Session,
  datasourceId: string,
  opts: {
    /** 租户 ID；缺省时回退到 session.tenantId。 */
    tenantId?: string;
    tables?: string[];
    concurrency?: number;
    /** 数据源类型：jdbc（默认）或 api。决定探列方式。 */
    datasourceType?: string;
    /** 收集 --tables 中在数据库里不存在的表名（用于给出「你是不是想找 X」提示）。 */
    onUnknownTables?: (unknown: string[], allTables: string[]) => void;
  } = {},
): Promise<TableSchema[]> {
  const tenantId = opts.tenantId ?? session.tenantId;
  if (!tenantId) throw new ApiError('buildSchema 需要 tenantId（可通过 opts.tenantId 或 session.tenantId 提供）', 400);
  const concurrency = Math.max(1, opts.concurrency ?? 5);
  const datasourceType = opts.datasourceType ?? 'jdbc';
  const isApi = datasourceType === 'api';

  // 始终先列出真实表名/数据集：即使用户用 --tables 限定了范围，也要据此核对拼写。
  // 直接拿用户给的名字去 select 会把「表名写错」退化成一条模糊的执行失败，
  // 而这正是要解决的问题，所以必须比对真实表清单。
  const all = await buildDatabaseTables(session, datasourceId, { tenantId, datasourceType });
  const allTables = all
    .map((t: any) =>
      typeof t === 'string'
        ? { name: t, type: undefined, id: undefined }
        : { name: t.name ?? t.tableName, type: t.type, id: t.id },
    )
    .filter((t: { name?: string }) => !!t.name) as { name: string; type?: string; id?: string }[];

  let targets: { name: string; type?: string; id?: string }[];
  if (opts.tables?.length) {
    // 大小写不敏感匹配：Oracle 惯用大写表名，用户多半按小写敲。
    const byLower = new Map(allTables.map((t) => [t.name.toLowerCase(), t]));
    const unknown: string[] = [];
    const picked = new Map<string, { name: string; type?: string; id?: string }>();
    for (const raw of opts.tables) {
      if (raw.includes('*')) {
        // 通配：t_emp* / *_log / *emp*，按整名匹配（非子串），避免误纳。
        const re = new RegExp(
          `^${raw.split('*').map((s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('.*')}$`,
          'i',
        );
        const hits = allTables.filter((t) => re.test(t.name));
        if (hits.length) for (const h of hits) picked.set(h.name, h);
        else unknown.push(raw);
        continue;
      }
      const hit = byLower.get(raw.toLowerCase());
      if (hit) picked.set(hit.name, hit);
      else unknown.push(raw);
    }
    targets = [...picked.values()];
    if (unknown.length) {
      opts.onUnknownTables?.(
        unknown,
        allTables.map((t) => t.name),
      );
    }
  } else {
    targets = allTables;
  }

  const out: TableSchema[] = new Array(targets.length);
  let cursor = 0;
  const worker = async () => {
    for (;;) {
      const i = cursor++;
      const t = targets[i];
      if (!t) return;
      try {
        const { fields } = await buildFields(session, datasourceId, `select * from ${t.name}`, {
          tenantId,
          limit: 1,
          datasourceType,
          // API 数据源按 datasetId 探列；JDBC 用 select，无需 datasetId。
          datasetId: isApi ? t.id : undefined,
        });
        out[i] = {
          name: t.name,
          type: t.type,
          columns: (fields ?? []).map((f) => ({ name: f.name, type: f.type })),
        };
      } catch (e) {
        out[i] = {
          name: t.name,
          type: t.type,
          columns: [],
          error: e instanceof Error ? e.message : String(e),
        };
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, targets.length) }, worker));
  return out;
}

/**
 * 加载租户下全部已配置服务器数据源的定义（无需先知道 id 即可浏览）。
 * 复用同一端点，但不传 datasourceId，期望后端返回全部数据源数组。
 * 端点：/baskserver/baskreport/api/server-datasource/loadDatasourceDefs?online=false&tenantId=<tenant>
 *   - 后端返回数组：直接作为 DatasourceDef[] 返回；
 *   - 后端返回单条对象（个别实现仍回单条）：包成单元素数组，保证 CLI 输出一致为列表。
 */
export async function loadAllDatasourceDefs(
  session: Session,
  opts: { online?: boolean; tenantId: string },
): Promise<DatasourceDef[]> {
  const online = opts.online ?? false;
  const tenantId = opts.tenantId;
  const url = `${serverDatasourceAction(session.baseUrl, 'loadDatasourceDefs')}?online=${online}&tenantId=${tenantId}`;
  const resp = await authedRequest(session, url, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded; charset=UTF-8' },
    body: '', // 不传 datasourceId，由后端返回全部
  });
  if (resp.statusCode !== 200) throw new ApiError(`加载全部数据源定义失败 (${resp.statusCode})`, resp.statusCode);
  const json = await parseJson(resp);
  const data = json.data ?? json;
  return Array.isArray(data) ? (data as DatasourceDef[]) : [data as DatasourceDef];
}

/**
 * 加载服务端数据源定义。
 * 端点：/baskserver/baskreport/api/server-datasource/loadDatasourceDefs?online=false&tenantId=<tenant>
 * 表单参数（经设计器抓包）：datasourceId=<id>，其中 id 通常是数据源主键或字符串 ID。
 * 返回数据源完整定义，可据此确认 IR 中 kind:server 所需的 id / type。
 */
export async function loadDatasourceDefs(
  session: Session,
  datasourceId: string | number,
  opts: { online?: boolean; tenantId: string },
): Promise<DatasourceDef> {
  const online = opts.online ?? false;
  const tenantId = opts.tenantId;
  const url = `${serverDatasourceAction(session.baseUrl, 'loadDatasourceDefs')}?online=${online}&tenantId=${tenantId}`;
  const body = new URLSearchParams({ datasourceId: String(datasourceId) }).toString();
  const resp = await authedRequest(session, url, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded; charset=UTF-8' },
    body,
  });
  if (resp.statusCode !== 200) throw new ApiError(`加载数据源定义失败 (${resp.statusCode})`, resp.statusCode);
  const json = await parseJson(resp);
  const data = json.data ?? json;
  if (data && typeof data === 'object' && !Array.isArray(data)) return data as DatasourceDef;
  // 后端可能忽略 id 直接返回全部数据源数组：按传入 id 精确匹配，避免错取首个。
  if (Array.isArray(data)) {
    const idStr = String(datasourceId).trim();
    const hit = data.find(
      (d) => d && (String(d.id) === idStr || String(d.datasourceId) === idStr || String(d.name) === idStr),
    );
    if (hit) return hit as DatasourceDef;
    // 匹配不到时仍退化为首个，但给出明确告警，便于发现服务端未过滤。
    console.error(
      `警告：loadDatasourceDefs 返回了 ${data.length} 个数据源，但无一个 id/name 等于 "${idStr}"。` +
        `将退化为取首个（${data[0]?.id ?? '?'}），请检查服务端是否正确按 datasourceId 过滤。`,
    );
    return data[0] as DatasourceDef;
  }
  return data;
}

/**
 * 查询已登记数据集字段（供生成 SQL 与绑定字段参考）。
 * 注意：仅对已登记、带整型 datasetId 的数据集有效；对即时映射的表/视图请改用 buildFields。
 */
export async function listFields(
  session: Session,
  datasetId: string | number,
): Promise<FieldInfo[]> {
  const url = `${datasourceAction(session.baseUrl, 'fields')}?datasetId=${datasetId}`;
  const resp = await authedRequest(session, url, { method: 'GET' });
  if (resp.statusCode !== 200) throw new ApiError(`列字段失败 (${resp.statusCode})`, resp.statusCode);
  const json = await parseJson(resp);
  return json.data ?? json;
}

/**
 * 抽取数据集样例数据（预览字段取值，辅助 SQL 编写）。
 * 注意：仅对已登记、带整型 datasetId 的数据集有效。
 */
export async function sampleData(
  session: Session,
  datasetId: string | number,
  parameters: Record<string, string> = {},
): Promise<any[]> {
  const qs = new URLSearchParams(parameters as Record<string, string>).toString();
  const url = `${datasourceAction(session.baseUrl, 'data')}?datasetId=${datasetId}${qs ? `&${qs}` : ''}`;
  const resp = await authedRequest(session, url, { method: 'GET' });
  if (resp.statusCode !== 200) throw new ApiError(`取样例数据失败 (${resp.statusCode})`, resp.statusCode);
  const json = await parseJson(resp);
  return json.data ?? json;
}

/**
 * 加载 BeanDataset 数据集名称列表（BeanDataset 没有数据源，无需 datasource-id）。
 * 端点：/baskserver/baskreport/api/datasource-bean/loadDatasets?online=false&tenantId=<tenant>
 * 后端返回结构（实测契约）：根节点仅含 `categories` 数组，分类下挂数据集名称：
 *   {
 *     "categories": [
 *       { "name": "示例", "datasets": ["自定义员工表","执行任务统计","主机执行任务"] },
 *       { "name": "账单", "datasets": ["账单明细","账单"] },
 *       ...
 *     ]
 *   }
 * 本函数从所有 `categories[].datasets` 聚合出扁平、去重的全量名称数组
 * （供 CLI 列表 / --index 选取 / 回源校验）。
 */
export async function loadBeanDatasets(
  session: Session,
  opts: { online?: boolean; tenantId: string },
): Promise<string[]> {
  const online = opts.online ?? false;
  const url = `${beanDatasourceAction(session.baseUrl, 'loadDatasets')}?online=${online}&tenantId=${opts.tenantId}`;
  const resp = await authedRequest(session, url, { method: 'GET' });
  if (resp.statusCode !== 200) throw new ApiError(`加载 BeanDataset 列表失败 (${resp.statusCode})`, resp.statusCode);
  const json = await parseJson(resp);

  // 根节点 categories:[{ name, datasets:string[] }] —— 聚合所有分类下的名称
  const names = new Set<string>();
  if (Array.isArray(json?.categories)) {
    for (const cat of json.categories) {
      if (cat && Array.isArray(cat.datasets)) {
        for (const n of cat.datasets) if (typeof n === 'string') names.add(n);
      }
    }
  }
  return [...names];
}

/**
 * 加载 BeanDataset 数据集的字段（按数据集名称，无需 datasource-id / 整型 datasetId）。
 * 端点：/baskserver/baskreport/api/datasource-bean/loadFields?online=false&tenantId=<tenant>
 * 表单参数（经实测契约）：datasets=<数据集名称>（如 主机执行任务）。
 * 返回结构（经实测契约）：以数据集名称为 key 的对象，
 *   如 { "主机执行任务": { "data": [], "fields": [ { "name": "host", "label": "主机", "type": "String" }, ... ] } }。
 * 类型归一化：type 首字母大写的值（如 String / Integer / Float / Boolean / Date）统一映射为
 * IR FieldType 小写枚举（string / number / date / boolean），并与报表 XML 示例对齐；
 * 未知类型按原值小写返回（交由下游校验拦截）。
 */
export async function loadBeanFields(
  session: Session,
  datasetName: string,
  opts: { online?: boolean; tenantId: string },
): Promise<FieldInfo[]> {
  const online = opts.online ?? false;
  const url = `${beanDatasourceAction(session.baseUrl, 'loadFields')}?online=${online}&tenantId=${opts.tenantId}`;
  const body = new URLSearchParams({ datasets: datasetName }).toString();
  const resp = await authedRequest(session, url, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded; charset=UTF-8' },
    body,
  });
  if (resp.statusCode !== 200) throw new ApiError(`加载 BeanDataset 字段失败 (${resp.statusCode})`, resp.statusCode);
  const json = await parseJson(resp);
  const entry = json?.[datasetName];
  const raw = entry?.fields ?? [];
  // BeanDataset 后端字段 type 为 Java 类型（String/Integer/Float/Boolean/Date...），
  // 统一归一化为 IR FieldType 小写枚举（string/number/date/boolean）。
  const TYPE_MAP: Record<string, string> = {
    // 字符串族
    string: 'string',
    char: 'string',
    varchar: 'string',
    text: 'string',
    // 数值族
    integer: 'number',
    int: 'number',
    long: 'number',
    short: 'number',
    byte: 'number',
    float: 'number',
    double: 'number',
    decimal: 'number',
    bigdecimal: 'number',
    number: 'number',
    // 布尔族
    boolean: 'boolean',
    bool: 'boolean',
    // 日期族
    date: 'date',
    datetime: 'date',
    timestamp: 'date',
    time: 'date',
  };
  const norm = (t?: string) => (t ? (TYPE_MAP[t.toLowerCase()] ?? t.toLowerCase()) : t);
  const fields: FieldInfo[] = Array.isArray(raw)
    ? raw.map((f: any) => ({ ...f, type: norm(f.type) }))
    : [];
  return fields;
}

/**
 * 表达式函数库：加载租户下设计器可用的全部函数（生成表达式前调用，
 * 确保函数名 / 参数个数 / 语法与真实可用一致，避免臆造不存在的函数）。
 * 端点：/baskserver/baskreport/api/designer/loadFunctions?online=false&tenantId=<tenant>
 * GET（实测契约）：返回 JSON 数组，每项含
 *   name            函数名（表达式实际书写，如 sum / datedif / dataset / getkeyvalue）
 *   label           中文显示名（可选）
 *   category        分类（如 常用函数 / 报表函数 / 数学函数 / 日期和时间函数 / 文本函数 / 其它函数）
 *   parametersCount 参数个数描述（如 "1"、">=1"、"=4"、"<=1"、"=0"、">0"）
 *   desc            语法与说明（可能含 <br> 富文本标记）
 * 兼容 {data:[...]} / {functions:[...]} 等包裹形态。
 */
export interface ReportFunction {
  name: string;
  label?: string;
  category?: string;
  parametersCount?: string;
  desc?: string;
  [key: string]: any;
}

export async function loadFunctions(
  session: Session,
  opts: { online?: boolean; tenantId: string },
): Promise<ReportFunction[]> {
  const online = opts.online ?? false;
  const url = `${designerAction(session.baseUrl, 'loadFunctions')}?online=${online}&tenantId=${opts.tenantId}`;
  const resp = await authedRequest(session, url, { method: 'GET' });
  if (resp.statusCode !== 200) throw new ApiError(`加载表达式函数库失败 (${resp.statusCode})`, resp.statusCode);
  const json = await parseJson(resp);
  const data = json.data ?? json;
  const funcs = Array.isArray(data) ? data : (data.functions ?? []);
  return Array.isArray(funcs) ? (funcs as ReportFunction[]) : [];
}

/**
 * 批量表达式语法在线校验（baskserver /designer/checkSyntaxBatch，供 AI Agent 使用）。
 * 端点：POST /baskserver/baskreport/api/designer/checkSyntaxBatch?online=false&tenantId=<tenant>
 * body 为顶层 JSON 数组：[{ "id": "A1", "expr": "=sum(1,2)" }, ...]
 *   - id：调用方自定义标识（如单元格坐标），用于回填定位；缺省时服务端用序号兜底
 *   - expr：可带 "=" 前缀（服务端自动剥离）；空串返回 "表达式为空"
 * 返回：{ total, okCount, errorCount, results: [{ index, id, ok, error }] }
 *   - 服务端单批上限 500 条，超出整体拒绝（error 字段说明）；调用方应自行分批。
 */
export interface CheckSyntaxBatchItem {
  /** 自定义标识（如单元格坐标 A2），回填到 results[].id 用于精确定位 */
  id?: string;
  /** 表达式，可带 "=" 前缀 */
  expr: string;
}

export interface CheckSyntaxBatchResult {
  total?: number;
  okCount?: number;
  errorCount?: number;
  results?: Array<{
    index?: number;
    id?: string;
    ok?: boolean;
    error?: string | null;
  }>;
  /** 请求级错误（body 非法 / 超过 500 条 / expressions 为空） */
  error?: string;
  [key: string]: any;
}

export async function checkExpressionSyntaxBatch(
  session: Session,
  items: CheckSyntaxBatchItem[],
  opts: { tenantId?: string } = {},
): Promise<CheckSyntaxBatchResult> {
  const tenantId = opts.tenantId ?? session.tenantId;
  const url = `${designerAction(session.baseUrl, 'checkSyntaxBatch')}?online=false&tenantId=${tenantId ?? ''}`;
  const resp = await authedRequest(session, url, {
    method: 'POST',
    headers: { 'content-type': 'application/json; charset=UTF-8' },
    body: JSON.stringify(items),
  });
  if (resp.statusCode !== 200) throw new ApiError(`批量表达式语法校验失败 (${resp.statusCode})`, resp.statusCode);
  const json = await parseJson(resp);
  const data = (json?.data ?? json) as CheckSyntaxBatchResult;
  return data;
}
